import React, { Fragment, useState, useEffect, useRef } from 'react';
import {
	Avatar,
	Badge,
	Box,
	Collapse,
	InputAdornment,
	List,
	ListItem,
	TextField,
	Tooltip,
	Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import '../../styles/pages/global-task/kpiApprovals.scss';
import { OKRButton } from '../Common/OKRButton';
import { RightArrow } from '../../config/svg/ArrowSvg';
import { ConversationIcon } from '../../config/svg/CommonSvg';
import { approveGoalKpi, getKpiPendingApprovalList } from '../../action/MyKpis';
import { Enums } from '../../config/enums';
import { useDispatch, useSelector } from 'react-redux';
import {
	getCurrencyIcon,
	getCurrencyName,
	getCycleDefaultSelection,
	getUserDetails,
	getUserName,
} from '../../config/utils';
import { ErrorIcon } from '../../config/svg/formElementIcons';
import { useSnackbar } from 'notistack';
import { KpiApprovalFilter } from './kpiApprovalFilter';
import { getDefaultKpiCategories } from '../../action/adminDefaultOkr';
import { getLocalStorageItem } from '../../services/StorageService';
import noRecordFound from '../../images/search-placeholder.svg';
import { OKRDrawer } from '../Common/OKRDrawer';
import { Cfr } from '../Goals/Cfr';
import {
	addConversation,
	deleteConversation,
	getConversation,
	likeConversation,
	UpdateConversation,
} from '../../action/cfr';
import { globalTaskNotificationRedirection } from '../../action/globalTask';

export const KpiApprovals: React.FC<any> = (props: any) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { okrMasterData } = useSelector((state: any) => state.okrReducer);
	const loggedInUserDetail = getUserDetails();

	const [pendingApprovalList, setPendingApprovalList] = useState<any[]>([]);
	const [expandedUsers, setExpandedUsers] = useState<number[]>([]);
	const [listLoading, setListLoading] = useState(false);
	const cycleId = getLocalStorageItem('cycleId2') ? parseInt(getLocalStorageItem('cycleId2') || '0') : 0;
	const [appliedFilter, setAppliedFilter] = useState<boolean>(false);
	const [categories, setCategories] = useState<any>([]);
	const [selectedCycleDetails, setSelectedCycleDetails] = useState<any>(null);

	const defaultSearchFilter: any = {
		kpiCategoryMasterId: [],
	};
	const [finalFilterCondition, setFinalFilterCondition] = useState<any>({ ...defaultSearchFilter });
	const { enqueueSnackbar } = useSnackbar();
	const { cycleDetailsForDefaultKpi } = useSelector((state: any) => state.kpiReducer);

	const { conversationList, getConversationStatus } = useSelector((state: any) => state.cfrReducer);
	const [openCFR, setOpenCFR] = useState<boolean>(false);
	const [isCfrUpdated, setIsCfrUpdated] = useState<boolean>(false);
	const [modalCfrData, setModalCfrData] = useState<any>({ type: 1, okr: {}, kr: {}, taggedUser: {} });
	const [isTaskModified, setIsTaskModified] = useState<boolean>(false);
	const [unsavedCfrProps, setUnsavedCfrProps] = React.useState<any>({ open: false, event: null, source: '' });
	const { isRedirectedFromNotification } = useSelector((state: any) => state.globalTaskReducer);

	useEffect(() => {
		if (
			cycleDetailsForDefaultKpi &&
			cycleDetailsForDefaultKpi.cycleDetails &&
			cycleDetailsForDefaultKpi.cycleDetails.length
		) {
			let currentQuarterDetails: any = getCycleDefaultSelection(cycleDetailsForDefaultKpi.cycleDetails, cycleId);
			if (currentQuarterDetails) {
				setSelectedCycleDetails({ ...currentQuarterDetails });
				//setCurrentCycleId(currentQuarterDetails?.organisationCycleId || currentCycleId);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cycleDetailsForDefaultKpi]);
	useEffect(() => {
		fetchKpiCategories();
	}, []);

	useEffect(() => {
		setFinalFilterCondition({ ...defaultSearchFilter });
		getKpiApprovalList();
	}, [cycleId]);

	useEffect(() => {
		if (appliedFilter) {
			getKpiApprovalList();
			setAppliedFilter(false);
		}
	}, [finalFilterCondition]);

	useEffect(() => {
		if (pendingApprovalList.length > 0) {
			const allEmployeeIds = pendingApprovalList.map((user) => user.userData.employeeId);
			setExpandedUsers(allEmployeeIds);
		}
	}, [pendingApprovalList]);

	const fetchKpiCategories = async () => {
		try {
			const response: any = await dispatch(getDefaultKpiCategories());
			if (response) {
				if (response.data && response.data.status === 200) {
					const { entity } = response.data;
					setCategories(entity);
				} else {
					setCategories([]);
				}
			}
		} catch (e) {
			console.error(e);
		}
	};
	const getKpiApprovalList = async () => {
		try {
			setListLoading(true);
			let requestParam: any = {
				cycleId: cycleId,
				employeeId: loggedInUserDetail.employeeId,
				categoryId:
					finalFilterCondition.kpiCategoryMasterId.length > 0
						? finalFilterCondition.kpiCategoryMasterId.map((item: any) => item.id)
						: [],
			};
			const response: any = await dispatch(getKpiPendingApprovalList(requestParam));
			if (response && response?.data?.status === Enums.STATUS_SUCCESS) {
				setPendingApprovalList(response?.data?.entity?.pendingApprovalList || []);
				const { isRedirected, userId } = isRedirectedFromNotification;
				if (isRedirected && userId) {
					if (!expandedUsers.includes(userId)) {
						handleExpandCollapse(userId);
					}
					setTimeout(() => {
						dispatch(globalTaskNotificationRedirection({}));
					}, 5000);
				}
			} else {
				setListLoading(false);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setListLoading(false);
		}
	};

	const setStartTargetValue = async (
		userIndex: number,
		catIndex: number,
		goalKeyIndex: number,
		key = '',
		value = ''
	) => {
		let pendingApprovalListCpy = [...pendingApprovalList];
		if (key === 'startValue') {
			pendingApprovalListCpy[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[goalKeyIndex].startValue =
				value;
		} else if (key === 'targetValue') {
			pendingApprovalListCpy[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[
				goalKeyIndex
			].targetValue = value;
		}
		setPendingApprovalList(pendingApprovalListCpy);
	};

	const showApiMsgs = (msg: string, variant: any) => {
		enqueueSnackbar(msg, {
			variant: variant,
			autoHideDuration: 5000,
		});
	};
	const approveKpi = async (userIndex: number, catIndex: number, goalKeyIndex: number) => {
		let payload: any = {
			goalKeyId:
				pendingApprovalList[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[goalKeyIndex].goalKeyId,
			startValue:
				pendingApprovalList[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[goalKeyIndex].startValue,
			targetValue:
				pendingApprovalList[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[goalKeyIndex].targetValue,
			workFlowId:
				pendingApprovalList[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[goalKeyIndex].workFlowId,
			workFlowTypeId:
				pendingApprovalList[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails[goalKeyIndex]
					.workFlowTypeId,
		};
		const response: any = await dispatch(approveGoalKpi(payload));
		if (response && response?.data?.status === Enums.STATUS_SUCCESS) {
			let pendingApprovalListCpy = [...pendingApprovalList];
			pendingApprovalListCpy[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails.splice(goalKeyIndex, 1);
			if (pendingApprovalListCpy[userIndex].allKpiDashBoardKpiKrResponse[catIndex].myGoalsDetails.length === 0) {
				pendingApprovalListCpy[userIndex].allKpiDashBoardKpiKrResponse.splice(catIndex, 1);
			}
			if (pendingApprovalListCpy[userIndex].allKpiDashBoardKpiKrResponse.length === 0) {
				pendingApprovalListCpy.splice(userIndex, 1);
			}
			setPendingApprovalList(pendingApprovalListCpy || []);
			const responseAPI = response.data.messageList;
			const keys = Object.keys(responseAPI);
			const messages = keys.map((item) => responseAPI[item]);
			showApiMsgs(`${messages} `, 'success');
		} else {
			const responseAPI = response.data.messageList;
			const keys = Object.keys(responseAPI);
			const messages = keys.map((item) => responseAPI[item]);
			showApiMsgs(`${messages} `, 'error');
		}
	};
	if (listLoading) {
		return (
			<Box className='loading-center-global'>
				<Typography>Loading...</Typography>
			</Box>
		);
	}

	const handleExpandCollapse = (employeeId: number) => {
		setExpandedUsers((prev) =>
			prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
		);
	};

	const isFilterApplied = () => {
		return finalFilterCondition && finalFilterCondition?.kpiCategoryMasterId.length > 0;
	};

	const getConversationData = (data: any) => {
		dispatch(getConversation(data));
	};
	const addConversationData = (data: any) => {
		return dispatch(addConversation(data));
	};
	const updateConversationData = (data: any) => {
		return dispatch(UpdateConversation(data));
	};
	const likeConversationData = (data: any) => {
		return dispatch(likeConversation(data));
	};
	const deleteConversationData = (data: any) => {
		return dispatch(deleteConversation(data));
	};

	const handleCfrDrawerClose = (event: any, type: string) => {
		event.preventDefault();
		event.stopPropagation();
		if (type === 'save') {
			event.preventDefault();
			setOpenCFR(false);
			if (modalCfrData.type === 1 && modalCfrData?.okr?.isUnreadConversation === true) {
				modalCfrData.okr.isUnreadConversation = false;
			} else if (modalCfrData.type === 2 && modalCfrData?.kr?.isUnreadConversation === true) {
				modalCfrData.kr.isUnreadConversation = false;
			}
			//props.resetConversation();
			//props.resetNotes();
		} else if (type === 'cancel') {
			if (isTaskModified) {
				setUnsavedCfrProps({ open: true, event: null, source: 'close' });
			} else {
				setOpenCFR(false);
				setUnsavedCfrProps({ open: false, event: null, source: '' });
			}
		}
	};
	const handleCfrOpen = (
		event: any,
		type: number,
		kr: any,
		okr: any,
		tabToOpen?: string,
		taggedUser = {},
		userData = {}
	) => {
		if (Boolean(okr) || Boolean(kr)) {
			setOpenCFR(true);
			setModalCfrData({
				type: type,
				okr: okr,
				kr: { ...kr, source: kr.goalKeyId },
				taggedUser: taggedUser,
				userData: [userData],
			});
		}
	};

	return (
		<Fragment>
			{(!listLoading && pendingApprovalList.length > 0) || isFilterApplied() ? (
				<Box className='kpi-approvals-head'>
					<Box className='kpi-approvals-left'>
						<Typography variant='h2'>{t('approvalsLabel')}</Typography>
					</Box>
					<Box className='kpi-approvals-right'>
						<KpiApprovalFilter
							categories={categories}
							appliedFilter={appliedFilter}
							setAppliedFilter={setAppliedFilter}
							setFinalFilterCondition={setFinalFilterCondition}
							finalFilterCondition={finalFilterCondition}
							defaultSearchFilter={defaultSearchFilter}
						/>
					</Box>
				</Box>
			) : (
				<></>
			)}
			{listLoading ? (
				<Box className='loading-center'>
					<Typography>{t('loading')}</Typography>
				</Box>
			) : pendingApprovalList.length === 0 ? (
				isFilterApplied() ? (
					<Box className='no-records-area no-kpis-area'>
						<Box className='no-record-message'>
							<Typography variant='h2' component='h2'>
								{t('filterNoPendingApprovalsFound')}
							</Typography>
							<img width='440' alt={t('globalSearchNoRecord')} src={noRecordFound} />
						</Box>
					</Box>
				) : selectedCycleDetails?.cycleStatus !== 1 ? (
					<Box className='no-records-area no-kpis-area'>
						<Box className='no-record-message'>
							<Typography variant='h2' component='h2'>
								{t('noPendingApprovalsFound')}
							</Typography>
							<img width='440' alt={t('globalSearchNoRecord')} src={noRecordFound} />
						</Box>
					</Box>
				) : (
					<Box className='no-records-area no-kpis-area'>
						<Box className='no-record-message'>
							<Typography variant='h2' component='h2'>
								{t('noPendingApprovalsFound')}
							</Typography>
							<img width='440' alt={t('globalSearchNoRecord')} src={noRecordFound} />
						</Box>
					</Box>
				)
			) : (
				pendingApprovalList.map((user: any, userIndex: number) => (
					<Box key={user.userData.employeeId} className='kpi-approvals-body'>
						<Box className='kpi-approvals-list-head'>
							<OKRButton
								className={`kr-expand-collapse-btn ${expandedUsers.includes(user.userData.employeeId) ? 'active' : ''}`}
								title={expandedUsers.includes(user.userData.employeeId) ? t('collapseLabel') : t('expendLabel')}
								icon={<RightArrow />}
								handleClick={() => handleExpandCollapse(user.userData.employeeId)}
							/>
							<Box className='user-info'>
								<Box className='user-img'>
									<Avatar
										className='avatar-default'
										key={`user-icon-${user.userData.employeeId}`}
										src={user.userData.imagePath || ''}
										alt={user.userData.firstName}
									>
										{getUserName({
											firstName: user.userData.firstName || '',
											lastName: user.userData.lastName || '',
											fullName: '',
										})}
									</Avatar>
								</Box>
							</Box>
							<Box className='user-info-details'>
								<Typography variant='h4'>{`${user.userData.firstName} ${user.userData.lastName}`}</Typography>
								<Typography variant='subtitle2'>{user.userData.emailId}</Typography>
							</Box>
						</Box>
						<Collapse
							className='kpi-approvals-list-content'
							in={expandedUsers.includes(user.userData.employeeId)}
							timeout='auto'
							unmountOnExit
						>
							{user.allKpiDashBoardKpiKrResponse.map((category: any, catIndex: number) => (
								<Box key={category.goalObjectiveId} className='kpi-approvals-list'>
									<Typography variant='h4'>{category.objectiveName}</Typography>
									{category.myGoalsDetails.map((kpi: any, kpiIndex: number) => (
										<List
											key={kpi.goalKeyId}
											className={` ${
												isRedirectedFromNotification?.goalKeyId === kpi.goalKeyId ? ' highlighted-okr' : ''
											}`}
											id={`kpi_list_item_index_${user.userData.employeeId}_${category?.goalObjectiveId}_${kpi.goalKeyId}`}
											disablePadding
										>
											<ListItem className='kpi-approvals-list-col1'>
												<Box className='kpi-name'>
													<Tooltip arrow title={kpi.keyDescription}>
														<Typography>{kpi.keyDescription}</Typography>
													</Tooltip>
												</Box>
											</ListItem>
											<ListItem className='kpi-approvals-list-col2'>
												<Box className='kpi-metrics-fileds'>
													<Box className='kpi-metrics-fileds-inner'>
														<TextField
															className='current-value helperText-bottom'
															id={`kpi-start-value-${kpi.goalKeyId}`}
															placeholder={`${kpi.startValue}`}
															value={kpi.startValue}
															onChange={(e) => {
																setStartTargetValue(userIndex, catIndex, kpiIndex, 'startValue', e.target.value);
															}}
															error={kpi.errorStartMessage ? true : false}
															helperText={
																kpi.errorStartMessage && (
																	<React.Fragment>
																		<ErrorIcon />
																		{kpi.errorStartMessage}
																	</React.Fragment>
																)
															}
															fullWidth
															InputProps={{
																startAdornment: (
																	<InputAdornment position='start'>
																		{kpi?.metricType === 1
																			? '%'
																			: kpi?.metricType === 2
																			? getCurrencyIcon(
																					getCurrencyName(kpi?.metricType, kpi?.currencyId, okrMasterData)
																			  )
																			: '#'}
																	</InputAdornment>
																),
															}}
														/>
														<Box className='matrics-field-seprator' component='span'>
															{'/'}
														</Box>
														<TextField
															className='target-value helperText-bottom'
															id={`kpi-end-value-${kpi.goalKeyId}`}
															placeholder={`${kpi.targetValue}`}
															value={kpi.targetValue}
															onChange={(e) => {
																setStartTargetValue(userIndex, catIndex, kpiIndex, 'targetValue', e.target.value);
															}}
															error={kpi.errorTargetMessage ? true : false}
															helperText={
																kpi.errorTargetMessage && (
																	<React.Fragment>
																		<ErrorIcon />
																		{kpi.errorTargetMessage}
																	</React.Fragment>
																)
															}
															fullWidth
															InputProps={{
																startAdornment: (
																	<InputAdornment position='start'>
																		{kpi?.metricType === 1
																			? '%'
																			: kpi?.metricType === 2
																			? getCurrencyIcon(
																					getCurrencyName(kpi?.metricType, kpi?.currencyId, okrMasterData)
																			  )
																			: '#'}
																	</InputAdornment>
																),
															}}
														/>
													</Box>
												</Box>
												<Box className='kpi-approvals-actions'>
													<OKRButton
														className={'btn-primary'}
														id='kpi-target-approve-btn'
														text={t('approveLabel')}
														handleClick={() => {
															approveKpi(userIndex, catIndex, kpiIndex);
														}}
													/>
													<OKRButton
														className={`conversation-btn`}
														icon={
															<Badge variant='dot' className='dot-bubble' overlap='circular'>
																<ConversationIcon />
															</Badge>
														}
														handleClick={(e: any) => {
															handleCfrOpen(e, 5, kpi, category, 'conversation', {}, user.userData);
															setOpenCFR(true);
														}}
														title={t('conversationLabel')}
													/>
												</Box>
											</ListItem>
										</List>
									))}
								</Box>
							))}
						</Collapse>
					</Box>
				))
			)}

			{openCFR && (
				<OKRDrawer
					open={openCFR}
					drawerClassName={'main-drawer-panel main-drawer-titlepanel ok-cfr-panel'}
					transitionDuration={{ enter: 500, exit: 1000 }}
					headerTitle={''}
					drawerHead={false}
					children={
						<Cfr
							isCfrUpdated={isCfrUpdated}
							setIsCfrUpdated={setIsCfrUpdated}
							{...props}
							kr={modalCfrData.kr}
							okr={modalCfrData.okr}
							type={modalCfrData.type}
							taggedUser={modalCfrData.taggedUser}
							userListingData={modalCfrData.userData}
							useStaticData={true}
							setIsTaskModified={setIsTaskModified}
							openCFR={openCFR}
							setOpenCFR={setOpenCFR}
							setUnsavedCfrProps={setUnsavedCfrProps}
							unsavedCfrProps={unsavedCfrProps}
							getConversation={getConversationData}
							addConversation={addConversationData}
							UpdateConversation={updateConversationData}
							likeConversation={likeConversationData}
							deleteConversation={deleteConversationData}
							conversationList={conversationList}
							getConversationStatus={getConversationStatus}
							selectTab={'conversation'}
						/>
					}
					moreButton={false}
					handleDrawerClose={(event: any) => handleCfrDrawerClose(event, 'cancel')}
					handleSaveClick={(event: any) => handleCfrDrawerClose(event, 'save')}
					handleCancelClick={(event: any) => handleCfrDrawerClose(event, 'cancel')}
					hideDefaultButton={true}
					isAnyDrawerOpened={props.isAnyDrawerOpened}
					appDrawerInfo={props.appDrawerInfo}
				/>
			)}
		</Fragment>
	);
};
